import { Component } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Button, ButtonGroup } from '@upmc/ims.component-library';

import StyleAuxiliary from '../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import { SubmitButton } from '../../components/UI/Input/Button/index';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import axiosInstance from '../../services/axios/axios';
import * as appsActions from '../../store/apps/actions';
import * as ancillaryActions from '../../store/ancillary/actions';
import { RequestTypes } from '../../constants/enums';
import { WrapImsPacket } from '../../store/utility';
import { getLegacyPath } from 'routes/_utils';
import msalConfig from 'services/msal/config';
import { acquireAccessToken } from 'services/msal/_utilities';
import NewSponsorSelection from './NewSponsorSelection/NewSponsor';
import { selectModifyQueue } from '../../store/uar/selectors';

const getAnnualReviewFlowRedirectPath = ({
	locationSearch,
	modifyQueue,
	trackingNumber,
}) => {
	const urlParams = new URLSearchParams(locationSearch || '');
	const currentTrackingNumber = String(
		urlParams.get('tn') || trackingNumber || ''
	);
	const preservedEsoFiId = String(urlParams.get('esoFiId') || '');
	const safeModifyQueue = Array.isArray(modifyQueue) ? modifyQueue : [];

	if (safeModifyQueue.length === 0) {
		const fallbackParams = new URLSearchParams({
			view: 'processed',
		});

		if (preservedEsoFiId) {
			fallbackParams.set('esoFiId', preservedEsoFiId);
		}

		return `/UserAccessReview/Review/ActionFlow?${fallbackParams.toString()}`;
	}

	const currentIndex = safeModifyQueue.findIndex(item => {
		return String(item?.trackingNumber || '') === currentTrackingNumber;
	});
	const resolvedCurrentIndex = currentIndex >= 0 ? currentIndex : 0;
	const nextIndex = resolvedCurrentIndex + 1;

	if (nextIndex < safeModifyQueue.length) {
		const nextItem = safeModifyQueue[nextIndex];
		const esoFiId =
			preservedEsoFiId ||
			safeModifyQueue
				.map(entry => Number(entry?.staffMemberId))
				.filter(id => Number.isFinite(id) && id > 0)
				.join(',');

		const params = new URLSearchParams({
			tn: nextItem.trackingNumber,
			uarSource: 'annualReview',
			uarTargetName: nextItem.staffMemberName,
			uarStep: String(nextIndex + 1),
			uarTotal: String(safeModifyQueue.length),
			esoFiId,
		});

		return `/UserAccessReview/Review/ActionFlow?${params.toString()}`;
	}

	const processedParams = new URLSearchParams({ view: 'processed' });
	if (preservedEsoFiId) {
		processedParams.set('esoFiId', preservedEsoFiId);
	}

	return `/UserAccessReview/Review/ActionFlow?${processedParams.toString()}`;
};

/**
 *  AppContainer class component:  container component to hold all Application Components
 */
class AppContainer extends Component {
	constructor(props) {
		super(props);
		this.state = {
			activeIndexRequested: null,
			cannotNext: false,
			dialogContent: null,
			isDialogOpen: false,
			invalidTabName: '',
			redirectURL: '',
		};

		this.handleNext = this.handleNext.bind(this);
		this.handlePrev = this.handlePrev.bind(this);
	}
	componentDidMount() {
		let allValid = true;
		for (let prop in this.props.children) {
			allValid = allValid && this.props.children[prop].props.validity;
		}
	}

	componentDidUpdate() {
		if (
			this.props.allFormsSubmitted &&
			!this.state.redirectURL &&
			!this.props.handleCompletionExternally
		) {
			//send completion notice to API
			// PATCH to the API
			const formPayload = {
				Completed: this.props.allFormsSubmitted,
			};
			const imsPacket = WrapImsPacket(formPayload);

			let baseURL = '';
			let redirectURL = '';

			switch (this.props.requestType) {
				case RequestTypes.ANNUAL_REVIEW:
					redirectURL = getAnnualReviewFlowRedirectPath({
						locationSearch: window.location.search,
						modifyQueue: this.props.modifyQueue,
						trackingNumber: this.props.trackingNumber,
					});
					break;
				case RequestTypes.MULTI_USER:
					acquireAccessToken(msalConfig.requests.api).then(token => {
						fetch('../Session/SetSessionValue/', {
							method: 'POST',
							credentials: 'same-origin',
							headers: {
								'Content-Type': 'application/json',
								Authorization: `Bearer ${token}`,
							},
							body: JSON.stringify({
								key: 'UsersList',
								value: this.props.esos
									.map(eso => eso.ID)
									.join(','),
							}),
						});
					});

					baseURL = getLegacyPath(
						'/RRS/Submit_MultiUserRequests.aspx?tn=',
					);

					redirectURL = baseURL + this.props.trackingNumber;
					break;
				case RequestTypes.SINGLE_USER:
					baseURL = getLegacyPath('/Confirm/Index?tn=');
					break;
				case RequestTypes.MULTI_SERVICE_ACCOUNT:
					redirectURL = '/Confirm';
					break;
				case RequestTypes.SERVICE_ACCOUNT:
					redirectURL = getLegacyPath(
						'/Service/ServiceAccountConfirmSubmit.aspx?tn=' +
							this.props.trackingNumber,
					);
					break;
				default:
					redirectURL = getLegacyPath(
						'/Confirm/Index?tn=' + this.props.trackingNumber,
					);
					break;
			}

			axiosInstance
				.patch(
					'/api/Applications/Requests/' +
						this.props.trackingNumber,
					imsPacket,
				)
				.then(response => {
					switch (response.status) {
						case 200:
							if (response.data.responseCode) {
								const responseCode =
									response.data.responseCode;
								switch (responseCode) {
									case 200:
										this.setState({
											redirectURL: redirectURL,
										});
										break;
									default:
										break;
								}
							}
							break;
						default:
							this.props.clearMessages();
							this.props.addMessage({
								Key: 3,
								Value: 'Error Completing Request.  Please refresh the page and submit again.',
							});
							break;
					}
				})
				.catch(error => {
					this.props.clearMessages();
					this.props.addMessage({
						Key: 3,
						Value: 'Error Completing Request.  Please refresh the page and submit again.',
					});
				});
		}
	}

	/**
	 * handleActiveIndexUpdate()
	 * TabBar.props.handleActiveIndexUpdate handler
	 * @param activeIndex index of requested tab
	 */
	handleActiveIndexUpdate = activeIndex => {
		this.props.updateIndex(activeIndex);
	};

	/**
	 * submitButtonClicked()
	 * onclick listener for the submit button
	 * @param {*} payload
	 * payload:
	 */
	handleNext() {
		// we will need current tab length
		let tabCount = Object.keys(this.props.allForms).length; //keys converts to array for count to be used on navigating requests
		tabCount = tabCount - 1;
		if (this.props.activeIndex < tabCount && this.props.activeIndex >= 0) {
			let newIndex = this.props.activeIndex;
			newIndex = newIndex + 1;
			if (!this.props.allForms[this.props.activeIndex].valid) {
				this.setState({
					isDialogOpen: true,
					activeIndexRequested: newIndex,
					dialogContent:
						this.props.allForms[this.props.activeIndex].name,
				});
			} else {
				this.handleActiveIndexUpdate(newIndex);
				this.setState({ cannotPrev: false });
				if (newIndex === tabCount) {
					this.setState({ cannotNext: true });
				}
			}
		} else {
			this.setState({ cannotNext: true });
		}
	}

	handlePrev() {
		// we will need current tab length
		let tabCount = Object.keys(this.props.allForms).length; //keys converts to array for count to be used on navigating requests
		tabCount = tabCount - 1;

		if (this.props.activeIndex <= tabCount && this.props.activeIndex >= 0) {
			let newIndex = this.props.activeIndex;
			newIndex = newIndex - 1;
			if (!this.props.allForms[this.props.activeIndex].valid) {
				this.setState({
					isDialogOpen: true,
					activeIndexRequested: newIndex,
					dialogContent:
						this.props.allForms[this.props.activeIndex].name,
				});
			} else {
				this.handleActiveIndexUpdate(newIndex);
				this.setState({ cannotNext: false });

				if (newIndex === 0) {
					this.setState({ cannotPrev: true });
				}
			}
		}
	}

	submitButtonClicked() {
		this.props.updateLoading({
			updatedValue: true,
		});
		this.props.updateSubmitting({
			updatedValue: true,
		});

		// if non-empty form(s) valid and change to form(s) required, check if any change to form(s) made
		const isModelsChanged = Object.keys(this.props.allForms).every(
			keyForm => {
				if (this.props.allForms[keyForm].submitted) {
					return this.props.allForms[keyForm].submitted;
				}
				const appData = this.props.appsData.find(
					app => app.id === this.props.allForms[keyForm].id,
				);
				const formInitial =
					appData &&
					String(appData.Action).toLowerCase() === 'modifyaccess' &&
					String(appData.ComponentType).toLowerCase() !== 'empty'
						? this.props.allFormsInitial.find(
								formInitial =>
									formInitial.id ===
									this.props.allForms[keyForm].id,
							)
						: null;
				return this.props.allFormValidity && formInitial
					? Object.keys(
							this.props.allForms[keyForm].model.members,
						).some(keyMember => {
							const keyInitial = Object.keys(
								formInitial.model.members,
							).find(keyInitial => keyInitial === keyMember);
							return keyInitial
								? this.props.allForms[keyForm].model.members[
										keyMember
									].value
									? formInitial.model.members[keyInitial]
											.value !==
										this.props.allForms[keyForm].model
											.members[keyMember].value
									: !!formInitial.model.members[keyInitial]
											.value
								: true;
						})
					: true;
			},
		);

		for (let form in this.props.allForms) {
			let contractModel = { ...this.props.allForms[form].model };
			contractModel.componentMap = null;
			contractModel.NewSponsorESOID = this.props.newSponsorESOID;
			let formData = {
				AppID: this.props.allForms[form].id,
				ContractModel: contractModel,
				ComponentType: this.props.allForms[form].componentType,
			};

			const payload = {
				TrackingNumber: this.props.trackingNumber,
				FormData: formData,
			};

			let allowSubmit =
				(this.props.allFormValidity && isModelsChanged) ||
				this.props.submitting;

			//set submitting
			this.props.updateSubmitting({
				updatedValue: true,
			});

			if (allowSubmit) {
				//dispatch a message to the banner
				this.props.clearMessages();
				this.props.addMessage({
					Key: 1,
					Value: 'Submission in progress. Please do not refresh browser or attempt to submit again.',
				});

				const imsPacket = WrapImsPacket(payload);

				axiosInstance
					.post(
						'/api/Applications/Requests/' +
							this.props.trackingNumber,
						imsPacket,
					)
					.then(response => {
						switch (response.status) {
							case 200:
								if (response.data.responseCode) {
									const responseCode =
										response.data.responseCode;
									const messages = response.data.messages;
									switch (responseCode) {
										case 200:
											this.props.clearMessages();
											this.props.appSubmissionSuccessful({
												formId: form,
											});
											messages.forEach(message => {
												this.props.addMessage(message);
											});
											break;
										default:
											this.props.clearMessages();
											messages.forEach(message => {
												this.props.addMessage(message);
											});
											break;
									}
								}
								break;
							default:
								break;
						}
					})
					.catch(error => {
						this.props.clearMessages();
						this.props.updateSubmitting({
							updatedValue: false,
						});
						this.props.updateLoading({
							updatedValue: false,
						});
						if (error.response) {
							switch (error.response.status) {
								case 400: //validation failure
									console.warn(
										'[AppContainer@submitButtonClicked] 400 Error',
										error,
										this.props,
										form,
									);
									if (
										typeof error.response.data === 'string'
									) {
										this.props.updateValidityOnError({
											formId: form,
											data: {
												ErrorMessage:
													error.response.data,
											},
										});
									} else {
										for (let i = 0; i < 1; i++) {
											this.props.updateValidityOnError({
												formId: form,
												data: error.response.data[i],
											});
										}
									}
									break;
								case 501: //app not implemented
									console.error(
										'[AppContainer@submitButtonClicked] 501 Error',
										error.response,
									);
									break;
								default:
									console.error(
										'[AppContainer@submitButtonClicked]',
										error.response.status,
										' Error',
										error.response,
									);
									break;
							}
						}
					})
					.then(() => {
						if (
							form ===
							Object.keys(this.props.allForms).length - 1
						) {
							this.updateSubmitting({ updatedValue: false });
						}
					});
			} else {
				//force validation routes to be invoked
				const formId = form;
				let members = this.props.allForms[formId].model.members;
				Object.keys(members).forEach(mem => {
					if (
						members[mem].validation.validationRoute &&
						members[mem].visible &&
						!this.props.allForms[formId].submitted
					) {
						this.props.callApiValidate({
							memberToValidateKey: mem,
							formToValidateKey: formId,
							allMembers: members,
							componentMap:
								this.props.allForms[formId].model.componentMap,
							trackingNumber: this.props.trackingNumber,
						});
					}
				});

				//force validation messages visible
				this.props.revealValidation({
					formId: form,
				});
				//disable loading animation
				this.props.updateLoading({
					updatedValue: false,
				});
				//unset submitting
				this.props.updateSubmitting({
					updatedValue: false,
				});

				//dispatch a message to the banner
				this.props.clearMessages();
				this.props.addMessage(
					!isModelsChanged && this.props.allFormValidity
						? {
								Key: 1,
								Value: 'Please ensure a modification has been made for each application requested.',
							}
						: {
								Key: 1,
								Value: 'Please complete all required fields for each application requested.',
							},
				);
			}
		}
	}

	render() {
		const isOnLastForm =
			this.props.activeIndex >=
			Object.keys(this.props.allForms).length - 1;
		const useNextAsPrimaryAction =
			this.props.useNextAsPrimaryAction && !isOnLastForm;
		const primaryActionLabel = useNextAsPrimaryAction ? 'Next' : 'Submit';
		const onPrimaryActionHandler = useNextAsPrimaryAction
			? () => this.handleNext()
			: () => this.submitButtonClicked();

		let newSponsor = (
			<NewSponsorSelection
				handleNewSponsorESOIDUpdate={this.props.updateNewSponsorESOID}
			/>
		);
		let dialog = (
			<Dialog
				open={this.state.isDialogOpen}
				onClose={() => this.setState({ isDialogOpen: false })}
			>
				<DialogTitle>Are you sure?</DialogTitle>
				<DialogContent>
					{'The ' + this.state.dialogContent + ' form is incomplete.'}
				</DialogContent>
				<DialogActions>
					<Button
						id={`app-container__button-group__incomplete-dialog__cancel`}
						onClick={() => this.setState({ isDialogOpen: false })}
						variant='text'
					>
						Cancel
					</Button>
					<Button
						id={`app-container__button-group__incomplete-dialog__accept`}
						onClick={() => {
							this.setState({ isDialogOpen: false });
							this.handleActiveIndexUpdate(
								this.state.activeIndexRequested,
							);
						}}
						variant='text'
					>
						Proceed
					</Button>
				</DialogActions>
			</Dialog>
		);
		return (
			<StyleAuxiliary classes={'container'}>
				{newSponsor}
				{this.props.children}
				<hr />
				<ButtonGroup center>
					<Button
						variant='outlined'
						id={`app-container__button-group__previous`}
						onClick={() => this.handlePrev()}
						disabled={this.props.activeIndex <= 0}
					>
						&lt;
					</Button>
					<Button
						variant='outlined'
						id={`app-container__button-group__next`}
						onClick={() => this.handleNext()}
						disabled={
							!(
								this.props.activeIndex <
								Object.keys(this.props.allForms).length - 1
							)
						}
					>
						&gt;
					</Button>
					<SubmitButton
						onClick={onPrimaryActionHandler}
						id={`app-container__button-group__submit`}
						sx={{ ml: '2.5rem' }}
						disabled={this.props.submitting || this.props.loading}
					>
						{primaryActionLabel}
					</SubmitButton>
				</ButtonGroup>
				{dialog}
				{this.state.redirectURL && (
					<Redirect to={this.state.redirectURL} push />
				)}
			</StyleAuxiliary>
		);
	}
}

/**
 * mapStateToProps
 * map redux globally stored state to local props
 * @param {*} state
 * state:  represents the redux state
 * @param {*} ownProps
 * ownProps: includes location from React Router
 */
const mapStateToProps = state => {
	return {
		allFormValidity: state.apps.allFormValidity,
		allFormsSubmitted: state.apps.allFormsSubmitted,
		allForms: state.apps.allForms,
		allFormsInitial: state.apps.allFormsInitial,
		appsData: state.apps.appsData,
		trackingNumber: state.apps.trackingNumber,
		activeIndex: state.apps.activeIndex,
		esos: state.ancillary.esos,
		requestType: state.apps.requestType,
		loading: state.apps.loading,
		submitting: state.apps.submitting,
		newSponsorESOID: state.apps.newSponsorESOID,
		modifyQueue: selectModifyQueue(state),
	};
};

const mapDispatchToProps = dispatch => {
	return {
		addMessage: message => dispatch(ancillaryActions.addMessage(message)),
		clearMessages: () => dispatch(ancillaryActions.clearMessages()),
		revealValidation: payload =>
			dispatch(appsActions.revealValidation(payload)),
		updateValidityOnError: payload =>
			dispatch(appsActions.updateValidity(payload)),
		appSubmissionSuccessful: appDetails =>
			dispatch(appsActions.appSubmitted(appDetails)),
		callApiValidate: route => dispatch(appsActions.callApiValidate(route)),
		updateIndex: index => dispatch(appsActions.updateIndex(index)),
		updateLoading: payload => dispatch(appsActions.updateLoading(payload)),
		updateSubmitting: payload =>
			dispatch(appsActions.updateSubmitting(payload)),
		updateNewSponsorESOID: ESOID =>
			dispatch(appsActions.updateNewSponsorESOID(ESOID)),
	};
};

/**
 * Export AppContainer component
 */
export default connect(mapStateToProps, mapDispatchToProps)(AppContainer);
